// src/tabs/DevicesTab.jsx
import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  Divider,
  Button,
  Box,
  Chip,
  IconButton,
} from "@mui/material";

import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import EventIcon from "@mui/icons-material/Event";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AddIcon from "@mui/icons-material/Add";
import VerifiedIcon from "@mui/icons-material/Verified";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import QrCodeIcon from "@mui/icons-material/QrCode";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";

import { formatDate, moneyINR } from "../../../utils/helpers";
import { useOutletContext } from "react-router-dom";

/* ─────────────────────────────────────────────
   Keyframes
───────────────────────────────────────────── */
const ANIMATION_STYLES = `
  @keyframes floatBlob {
    0%   { transform: translate(0px,  0px)  scale(1);    }
    33%  { transform: translate(30px,-30px) scale(1.05); }
    66%  { transform: translate(-20px,20px) scale(0.97); }
    100% { transform: translate(0px,  0px)  scale(1);    }
  }
  @keyframes floatBlob2 {
    0%   { transform: translate(0px,  0px)  scale(1);    }
    33%  { transform: translate(-25px,25px) scale(1.04); }
    66%  { transform: translate(20px,-15px) scale(0.98); }
    100% { transform: translate(0px,  0px)  scale(1);    }
  }
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
  @keyframes cardIn {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
`;

/* ─────────────────────────────────────────────
   Background blob
───────────────────────────────────────────── */
function GradientBlob({ sx }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "fixed",
        borderRadius: "50%",
        filter: "blur(72px)",
        pointerEvents: "none",
        zIndex: 0,
        ...sx,
      }}
    />
  );
}

/* ─────────────────────────────────────────────
   Warranty status badge
───────────────────────────────────────────── */
function WarrantyBadge({ isApproved, expiryDate }) {
  const expired = expiryDate && new Date(expiryDate) <= new Date();
  const active = isApproved && !expired;

  return (
    <Chip
      icon={
        active ? (
          <VerifiedIcon
            sx={{ fontSize: "12px !important", color: "#16a34a !important" }}
          />
        ) : (
          <ErrorOutlineIcon
            sx={{ fontSize: "12px !important", color: "#64748b !important" }}
          />
        )
      }
      label={active ? "Warranty Active" : expired ? "Expired" : "Inactive"}
      size="small"
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.3,
        bgcolor: active ? "#f0fdf4" : "#f1f5f9",
        color: active ? "#15803d" : "#475569",
        border: `1px solid ${active ? "#bbf7d0" : "#e2e8f0"}`,
        borderRadius: 0.2,
        flexShrink: 0,
        "& .MuiChip-icon": { ml: "5px" },
      }}
    />
  );
}

/* ─────────────────────────────────────────────
   Claim balance badge
───────────────────────────────────────────── */
function ClaimBadge({ count }) {
  return (
    <Chip
      label={`${count} Claim${count !== 1 ? "s" : ""}`}
      size="small"
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.3,
        bgcolor: "#eff6ff",
        color: "#2563eb",
        border: "1px solid #dbeafe",
        borderRadius: 0.2,
        flexShrink: 0,
      }}
    />
  );
}

/* ─────────────────────────────────────────────
   IMEI / Serial segmented pill
   Stacks vertically when 3 segments (xs screens)
───────────────────────────────────────────── */
function IdPill({ serial, imei1, imei2 }) {
  if (!serial && !imei1 && !imei2) return null;

  const hasThree = !!(serial && imei1 && imei2);

  // On xs with 3 fields → two rows: [Serial | IMEI1] and [IMEI2]
  // On sm+ → single row always
  if (hasThree) {
    return (
      <Stack spacing={0}>
        {/* Row 1: Serial + IMEI 1 */}
        <Stack
          direction="row"
          sx={{
            bgcolor: "#f8fafc",
            border: "1px solid #f1f5f9",
            borderRadius: "2px 2px 0 0",
            overflow: "hidden",
          }}
        >
          <PillSegment icon={<QrCodeIcon />} label="Serial" value={serial} />
          <Divider orientation="vertical" flexItem sx={{ borderColor: "#e8f0fe" }} />
          <PillSegment icon={<FingerprintIcon />} label="IMEI 1" value={imei1} />
        </Stack>
        {/* Row 2: IMEI 2 — full width */}
        <Stack
          direction="row"
          sx={{
            bgcolor: "#f8fafc",
            border: "1px solid #f1f5f9",
            borderTop: "none",
            borderRadius: "0 0 2px 2px",
            overflow: "hidden",
          }}
        >
          <PillSegment icon={<FingerprintIcon />} label="IMEI 2" value={imei2} fullWidth />
        </Stack>
      </Stack>
    );
  }

  // 1 or 2 segments → standard single-row pill
  return (
    <Stack
      direction="row"
      sx={{
        bgcolor: "#f8fafc",
        border: "1px solid #f1f5f9",
        borderRadius: 0.2,
        overflow: "hidden",
      }}
    >
      {serial && (
        <PillSegment icon={<QrCodeIcon />} label="Serial" value={serial} />
      )}
      {serial && imei1 && (
        <Divider orientation="vertical" flexItem sx={{ borderColor: "#e8f0fe" }} />
      )}
      {imei1 && (
        <PillSegment
          icon={<FingerprintIcon />}
          label={imei2 ? "IMEI 1" : "IMEI"}
          value={imei1}
        />
      )}
      {imei1 && imei2 && (
        <Divider orientation="vertical" flexItem sx={{ borderColor: "#e8f0fe" }} />
      )}
      {imei2 && (
        <PillSegment icon={<FingerprintIcon />} label="IMEI 2" value={imei2} />
      )}
    </Stack>
  );
}

/* One cell inside IdPill */
function PillSegment({ icon, label, value, fullWidth = false }) {
  return (
    <Stack
      direction="row"
      spacing={0.6}
      alignItems="center"
      sx={{
        px: { xs: 1, sm: 1.2 },
        py: 0.9,
        flex: fullWidth ? "1 1 100%" : 1,
        minWidth: 0,
      }}
    >
      <Box sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0, display: "flex" }}>
        {React.cloneElement(icon, { sx: { fontSize: 13, color: "#94a3b8" } })}
      </Box>
      <Box minWidth={0}>
        <Typography
          sx={{
            fontSize: 9,
            color: "#94a3b8",
            fontWeight: 600,
            letterSpacing: 0.8,
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 9, sm: 10 },
            fontWeight: 600,
            color: "#374151",
            fontFamily: "monospace",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value || "—"}
        </Typography>
      </Box>
    </Stack>
  );
}

/* ─────────────────────────────────────────────
   Single device card
───────────────────────────────────────────── */
function DeviceCard({ d, index, onRaiseClaim, onViewPurchase, onOpenDevice }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 0.2,
        border: "1px solid #e8f0fe",
        bgcolor: "#fff",
        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
        animation: "cardIn 0.32s ease both",
        animationDelay: `${index * 0.07}s`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        // Disable hover lift on touch devices (no hover)
        "@media (hover: hover)": {
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(0,0,0,0.09)",
          },
        },
      }}
    >
      <CardContent sx={{ p: { xs: "14px !important", sm: "16px !important" } }}>

        {/* ── Section 1: Identity ── */}
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            sx={{
              width: { xs: 40, sm: 44 },
              height: { xs: 40, sm: 44 },
              borderRadius: 0.2,
              bgcolor: "#f0fdf4",
              border: "1px solid #dcfce7",
              flexShrink: 0,
            }}
          >
            <PhoneIphoneIcon sx={{ color: "#16a34a", fontSize: { xs: 20, sm: 22 } }} />
          </Avatar>

          <Box flex={1} minWidth={0}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 13, sm: 14 },
                color: "#111827",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {d.product_name || "Device"}
            </Typography>

            <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: "#64748b", mt: 0.15 }}>
              {d.brand_name || "—"}&nbsp;·&nbsp;{d.category_name || "—"}
            </Typography>

            {/* Status chips — allow wrapping on tiny screens */}
            <Stack
              direction="row"
              spacing={0.8}
              mt={0.8}
              sx={{ flexWrap: "wrap", gap: 0.5 }}
            >
              <WarrantyBadge isApproved={d.is_approved} expiryDate={d.expiry_date} />
              <ClaimBadge count={d.available_claim ?? 0} />
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ my: 1.4, borderColor: "#f1f5f9" }} />

        {/* ── Section 2: Meta ── */}
        <Stack spacing={1}>

          {/* Expiry */}
          <Stack direction="row" spacing={0.7} alignItems="center">
            <EventIcon sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }} />
            <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: "#475569" }}>
              Warranty Expiry:{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "#111827" }}>
                {d.expiry_date ? formatDate(d.expiry_date) : "—"}
              </Box>
            </Typography>
          </Stack>

          {/* Warranty code */}
          {d.w_code && (
            <Stack direction="row" spacing={0.7} alignItems="center">
              <AssignmentTurnedInIcon sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }} />
              <Typography
                sx={{
                  fontSize: { xs: 10, sm: 11 },
                  color: "#475569",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Code:{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 600, color: "#374151", fontFamily: "monospace" }}
                >
                  {d.w_code}
                </Box>
              </Typography>
            </Stack>
          )}

          {/* IMEI / Serial pill — responsive 2-row layout for 3 fields */}
          <IdPill serial={d.serial} imei1={d.imei1} imei2={d.imei2} />

        </Stack>

        {/* ── Section 3: Stats mini-cards ── */}
        <Stack direction="row" spacing={1} sx={{ mt: 1.6 }}>
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 0.2,
              border: "1px solid #f1f5f9",
              bgcolor: "#fafafa",
            }}
          >
            <CardContent
              sx={{ py: 1, px: { xs: 1.2, sm: 1.4 }, "&:last-child": { pb: 1 } }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center" mb={0.2}>
                <CurrencyRupeeIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                <Typography
                  sx={{
                    fontSize: 9,
                    color: "#94a3b8",
                    fontWeight: 600,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                  }}
                >
                  Device Price
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 12, sm: 13 },
                  color: "#111827",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {moneyINR(d.device_price || 0)}
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 0.2,
              border: "1px solid #f1f5f9",
              bgcolor: "#fafafa",
            }}
          >
            <CardContent
              sx={{ py: 1, px: { xs: 1.2, sm: 1.4 }, "&:last-child": { pb: 1 } }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center" mb={0.2}>
                <AssignmentTurnedInIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                <Typography
                  sx={{
                    fontSize: 9,
                    color: "#94a3b8",
                    fontWeight: 600,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                  }}
                >
                  Claim Balance
                </Typography>
              </Stack>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: 12, sm: 13 }, color: "#111827" }}>
                {d.available_claim ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        {/* ── Section 4: Actions ──
            Mobile layout:
              [Purchases]  [Raise Claim]       [Details →]
            On very narrow screens the left pair wraps above Details.
        ── */}
        <Divider sx={{ my: 1.4, borderColor: "#f1f5f9" }} />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ flexWrap: "wrap", gap: 1 }}
        >
          {/* Left group: secondary + primary */}
          <Stack direction="row" spacing={0.8} sx={{ flexShrink: 0 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ListAltIcon sx={{ fontSize: "13px !important" }} />}
              onClick={() => onViewPurchase?.(d.id)}
              sx={{
                textTransform: "none",
                fontSize: { xs: 10, sm: 11 },
                fontWeight: 600,
                borderRadius: 0.2,
                borderColor: "#e2e8f0",
                color: "#475569",
                bgcolor: "#f8fafc",
                // 40px minimum touch target height
                minHeight: 36,
                px: { xs: 1, sm: 1.5 },
                "&:hover": {
                  borderColor: "#cbd5e1",
                  bgcolor: "#f1f5f9",
                },
              }}
            >
              Purchases
            </Button>

            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: "13px !important" }} />}
              onClick={() => onRaiseClaim?.(d.id)}
              sx={{
                textTransform: "none",
                fontSize: { xs: 10, sm: 11 },
                fontWeight: 700,
                borderRadius: 0.2,
                minHeight: 36,
                px: { xs: 1, sm: 1.5 },
                background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                boxShadow: "0 2px 8px rgba(22,163,74,0.28)",
                "&:hover": {
                  background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
                  boxShadow: "0 3px 12px rgba(22,163,74,0.36)",
                },
                "&:active": {
                  boxShadow: "0 1px 4px rgba(22,163,74,0.2)",
                },
              }}
            >
              Raise Claim
            </Button>
          </Stack>

          {/* Right: Details — text button, touch-friendly */}
          <Button
            size="small"
            endIcon={<ChevronRightIcon sx={{ fontSize: "14px !important" }} />}
            onClick={() => onOpenDevice?.(d.id)}
            sx={{
              textTransform: "none",
              fontSize: { xs: 10, sm: 11 },
              fontWeight: 600,
              borderRadius: 0.2,
              color: "#2563eb",
              minHeight: 36,
              px: 1,
              ml: "auto",
              "&:hover": { bgcolor: "#eff6ff" },
              // Ensure it stays on same line unless truly no room
              flexShrink: 0,
            }}
          >
            Details
          </Button>
        </Stack>

      </CardContent>
    </Card>
  );
}

/* ─────────────────────────────────────────────
   Main tab
───────────────────────────────────────────── */
export default function DevicesTab() {
  const {
    devices = [],
    onRaiseClaim,
    onViewPurchase,
    onOpenDevice,
  } = useOutletContext();

  const activeCount = devices.filter(
    (d) => d.is_approved && new Date(d.expiry_date) > new Date()
  ).length;

  return (
    <>
      <style>{ANIMATION_STYLES}</style>

      {/* Background blobs */}
      <GradientBlob
        sx={{
          width: { xs: 200, sm: 300 },
          height: { xs: 200, sm: 300 },
          top: -60,
          right: -80,
          background:
            "radial-gradient(circle, rgba(34,197,94,0.16) 0%, rgba(16,185,129,0.07) 70%)",
          animation: "floatBlob 20s ease-in-out infinite",
        }}
      />
      <GradientBlob
        sx={{
          width: { xs: 160, sm: 240 },
          height: { xs: 160, sm: 240 },
          bottom: 80,
          left: -70,
          background:
            "radial-gradient(circle, rgba(59,130,246,0.14) 0%, rgba(99,102,241,0.06) 70%)",
          animation: "floatBlob2 24s ease-in-out infinite",
        }}
      />

      {/* Content */}
      <Stack
        spacing={2}
        sx={{
          position: "relative",
          zIndex: 1,
          animation: "fadeSlideUp 0.4s ease both",
          // Critical: prevent any child from causing horizontal scroll
          overflowX: "hidden",
        }}
      >

        {/* Page header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: 16, sm: 17 },
                color: "#111827",
              }}
            >
              My Devices
            </Typography>
            <Stack direction="row" spacing={0.8} alignItems="center" mt={0.3}>
              <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: "#64748b" }}>
                {devices.length} device{devices.length !== 1 ? "s" : ""}
              </Typography>
              {activeCount > 0 && (
                <>
                  <Box
                    sx={{
                      width: 3,
                      height: 3,
                      borderRadius: "50%",
                      bgcolor: "#cbd5e1",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: 10, sm: 11 },
                      color: "#16a34a",
                      fontWeight: 600,
                    }}
                  >
                    {activeCount} active
                  </Typography>
                </>
              )}
            </Stack>
          </Box>
        </Stack>

        {/* Empty state */}
        {devices.length === 0 && (
          <Stack alignItems="center" justifyContent="center" spacing={1.2} py={6}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 0.2,
                bgcolor: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PhoneIphoneIcon sx={{ color: "#94a3b8", fontSize: 26 }} />
            </Box>
            <Typography sx={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>
              No devices found
            </Typography>
          </Stack>
        )}

        {/* Device cards */}
        {devices.map((d, index) => (
          <DeviceCard
            key={d.id}
            d={d}
            index={index}
            onRaiseClaim={onRaiseClaim}
            onViewPurchase={onViewPurchase}
            onOpenDevice={onOpenDevice}
          />
        ))}

      </Stack>
    </>
  );
}